import { HydratedZapAsset, Zap, ZapContext } from "@zapier/client";
import { BetaDataTableCell as Cell } from "@zapier/design-system";
import isUndefined from "lodash.isundefined";
import {
  DateCell,
  LocationCell,
  OwnerCell,
  Row,
  ServiceIconsWithTooltip,
  TitleCell,
  useRouterParams,
  ZapModal,
  ZapPauseToggle,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";
import {
  ApprovalType,
  AssetTypes,
  getEditZapUrl,
  getZapDisplayTitle,
  isUntitledZap,
} from "@zapier/utils";
import styles from "./ZapTable.module.css";
import { useZapRow } from "./useZapRow";

interface Props {
  zap: Zap;
  apps?: Record<string, any>;
  folderZapTableRow?: boolean;
  allowSelection?: boolean;
  searchQuery?: string | null;
  showLastRun?: boolean;
  setCurrentModal: (modal: ZapModal | undefined) => void;
  setApprovalType: (approvalType: ApprovalType | undefined) => void;
  setCurrentZap: (zap: Zap | HydratedZapAsset | undefined) => void;
  showReadOnlyZaps?: boolean;
  onCopyToAnotherAccount?: (asset: HydratedZapAsset) => void;
}

const ZapRow = ({
  zap,
  apps,
  folderZapTableRow,
  allowSelection,
  searchQuery,
  showLastRun,
  setCurrentModal,
  setApprovalType,
  setCurrentZap,
  showReadOnlyZaps,
  onCopyToAnotherAccount,
}: Props) => {
  const { lastRun } = useRouterParams();
  const t = useTranslations("last-run");
  const { menuItems, canEnableDisable, triggerPermissionsCheck } = useZapRow({
    zapId: zap.id,
    zap,
    lastRun: lastRun ?? undefined,
    setCurrentModal,
    setCurrentZap,
    onCopyToAnotherAccount,
  });

  const zapOwner = zap.owner?.fullName;
  const getFolderName = () => {
    if (zap.folderId) {
      return zap.folder?.name;
    }
    if (zapOwner) {
      return `${zapOwner} (Personal)`;
    }
    // Ensures that the visible folder name link (rendered inside LocationCell) is never empty (which would be an accessibility violation)
    return "Personal";
  };
  const isUntitled = isUntitledZap(zap.title);
  const zapTitle = getZapDisplayTitle(zap.title);

  return (
    <>
      <Row
        allowsSelection={allowSelection}
        assetType={AssetTypes.Zap}
        id={zap.id}
        isTrashed={!!zap.deletedAt}
        onOpenMenu={() => {
          triggerPermissionsCheck?.();
        }}
        menuItems={menuItems && !showReadOnlyZaps ? menuItems : []}
        assetName={zapTitle}
        kebabMenuLabel="Zap actions"
        kebabMenuAriaLabel={`Zap actions for ${zapTitle}`}
      >
        <TitleCell
          icon="miscBoltAltFill"
          isUntitled={isUntitled}
          title={zapTitle}
          url={zap.deletedAt ? undefined : getEditZapUrl(zap.id)}
          id={zap.id}
          folderZapTableRow={folderZapTableRow}
          searchQuery={searchQuery}
          assetType={AssetTypes.Zap}
        />
        <Cell className={styles.statusColumn}>
          {!!zap.id && !isUndefined(zap.isEnabled) && (
            <ZapPauseToggle
              zapDeletedAt={zap.deletedAt}
              zapIsPaused={!!zap.isPaused}
              zapIsEnabled={zap.isEnabled}
              zapZdl={zap.currentVersion?.zdl}
              zapCurrentVersionId={zap.currentVersion?.id}
              zapId={zap.id}
              zapTitle={zapTitle}
              setApprovalType={setApprovalType}
              setCurrentModal={setCurrentModal}
              setCurrentZap={() => setCurrentZap(zap)}
              isSharedFolder={zap.folder?.isShared}
              canEnableDisable={canEnableDisable && !showReadOnlyZaps}
            />
          )}
        </Cell>
        <Cell className={styles.appsColumn}>
          <ServiceIconsWithTooltip zap={zap} apps={apps} />
        </Cell>
        {!folderZapTableRow && (
          <LocationCell
            folderId={zap.folderId}
            folderName={getFolderName()}
            isSharedFolder={zap.folder?.isShared}
            ownerId={zap.ownerId}
            projectId={zap.projectId}
            projectName={zap.project?.title}
            className={styles.locationColumn}
            usePersonalFolder={true}
          />
        )}
        <DateCell
          datetime={zap.searchZap?.updatedAt || zap.updatedAt || new Date()}
        />
        {showLastRun && (
          <DateCell
            datetime={zap.searchZap?.lastRunAt}
            emptyLabel={t("empty-label")}
          />
        )}
        <OwnerCell name={zap.owner?.fullName} photoUrl={zap.owner?.photoUrl} />
      </Row>
    </>
  );
};

/**
 * This needs to be in a separate component so that the useZapActionPermissions
 * hook has access to the provider context when it is called.
 */
const ZapRowContainer = (props: Props) => {
  return (
    <ZapContext.Provider value={props.zap}>
      <ZapRow {...props} />
    </ZapContext.Provider>
  );
};

export default ZapRowContainer;
